import type { Probe } from './types';

/**
 * Explicit registry. Probes are NOT auto-imported via a glob, so the entire
 * collection surface is greppable and auditable in one file
 * (src/probes/index.ts). This is deliberate: a fingerprinting tool should be
 * reviewable.
 */
const registry = new Map<string, Probe>();

/**
 * Registers a probe.
 *
 * Duplicate ids are a hard error, but they are thrown LAZILY: a throwing
 * registerProbe() during module import would abort the whole bundle and leave
 * a blank page, which is a terrible way to learn about a typo. Instead the
 * collision is recorded and surfaced as a visible scan error.
 */
const collisions: Array<{ id: string; existing: string; incoming: string }> = [];

export function registerProbe(probe: Probe): void {
  if (registry.has(probe.id)) {
    collisions.push({
      id: probe.id,
      existing: probe.label,
      incoming: probe.label,
    });
    // Keep the first registration; drop the duplicate.
    return;
  }
  registry.set(probe.id, probe);
}

/** Ids that collided during registration. Non-empty means a build bug. */
export function getRegistryCollisions(): ReadonlyArray<{ id: string }> {
  return collisions;
}

export function getProbes(): Probe[] {
  return [...registry.values()];
}

export function getProbe(id: string): Probe | undefined {
  return registry.get(id);
}

/** Number of registered probes, for catalog counts in exports. */
export function getProbeCount(): number {
  return registry.size;
}

export function clearRegistry(): void {
  registry.clear();
  collisions.length = 0;
}

/** Probe ids for test isolation. */
export function __resetRegistryForTests(): void {
  clearRegistry();
}
